import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportHolidayDays } from './api/holidayDayApi'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import CardPaginator from '../../components/ui/CardPaginator'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import FilterBar from '../../components/ui/FilterBar'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { InputSwitch } from 'primereact/inputswitch'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { formatDate } from '../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import { holidayDayColumns } from './components/HolidayDayColumns'
import HolidayDayFormDialog from './components/HolidayDayFormDialog'
import { useHolidayDays } from './hooks/useHolidayDays'
import type { HolidayDay, HolidayDayUpsert } from './types'
import '../admin/admin.css'

/** Kart listesinin sayfa boyutu (anayasa: tüm listelerde 10 — DataTable de aynı varsayılanı kullanır). */
const CARD_PAGE_SIZE = 10

/** Arama gecikmesi (Page Standards) — her tuşta sunucuya gidilmez. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * Admin -> Parametreler -> Tatil Günü yönetim ekranı (23.09.2026). Liste (paylaşılan ui/DataTable) +
 * ekle/düzenle (FormDialog) + Aktif/Pasif anahtarı + soft-delete (onaylı) + Excel dışa aktarma.
 * Başlık = menü adı (nav.admin.holidayDays) — anayasa kuralı.
 *
 * Liste tarihe göre YENİDEN ESKİYE gelir (sunucu sıralar): takvimde güncel yıl üstte olmalı. Dar
 * ekranda tablo yerine KART listesi çizilir (anayasa: dar ekran kart kalıbı); eşik tek kaynaktan
 * (CARD_VIEW_BREAKPOINT). Aynı güne ikinci kayıt sunucuda engellenir ve hata tarih alanının altında
 * gösterilir — toast değil, çünkü kullanıcı hangi alanı düzelteceğini görmeli.
 *
 * SAYFALAMA İSTEMCİ TARAFINDADIR (takvim tek istekte gelir): geniş ekranda paylaşılan `ui/DataTable`
 * kendi paginatörünü yönetir, dar ekranda kart listesinin altına paylaşılan `ui/CardPaginator` konur.
 * Sunucu-taraflı (`lazy`) sayfalama BİLİNÇLİ OLARAK kullanılmadı: servis sırayı sabitliyor (tarih ↓) ve
 * kolon başlıklarındaki sıralama istemcide çalışıyor — lazy sayfalamada o başlıklar yalnız açık sayfayı
 * sıralar, yani kullanıcıya yanlış söylerdi.
 *
 * ARAMA ise SUNUCUDA süzülür (`q`): TR-farkında karşılaştırma (`i` ↔ `İ`) serviste yazılı, istemcide
 * ikinci bir kopyası tutulmaz. Açıklamada (TR+EN) arar; Excel dışa aktarımı da aynı metni taşır.
 */
export default function HolidayDaysPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  // 126 — Kısıtlı Yetkili asistan ekranı SALT OKUNUR görür: Yeni, anahtar, düzenle ve sil hiç
  // çizilmez; durum rozetle okunmaya devam eder.
  const { readOnly } = useProxyMode()
  const [cardFirst, setCardFirst] = useState(0)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [search])

  // Kart sayfası başa YALNIZ arama fiilen değişince döner — gecikme zamanlayıcısının İÇİNDE
  // sıfırlamak KARARSIZLIK üretiyordu: etki mount'ta da kurulduğu için, arama hiç değişmese bile
  // açılıştan SEARCH_DEBOUNCE_MS sonra koşulsuz bir `setCardFirst(0)` ateşliyordu. Kullanıcı (ya da
  // test) o 300 ms dolmadan 2. sayfaya geçerse gecikmiş zamanlayıcı onu 1. sayfaya geri atıyordu;
  // hangisinin önce olacağı makine hızına bağlıydı. THY'nin Jenkins'i 25.09.2026'da tam bu yüzden
  // kırmızı döndü (HolidayDaysPage.test.tsx → "dar ekranda ... paginator vardir"; yerelde 3 koşudan
  // 1'i düşüyordu). Burada `debounced` değişmediğinde React aynı değere bail-out yapar, etki
  // yeniden çalışmaz — sıfırlama yalnız gerçek arama değişiminde olur.
  useEffect(() => {
    setCardFirst(0)
  }, [debounced])

  const { list, create, update, changeStatus, remove } = useHolidayDays(debounced)
  const toast = useToast()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<HolidayDay | null>(null)
  const [dateError, setDateError] = useState<string | null>(null)

  const items = list.data?.content ?? []
  const cardItems = items.slice(cardFirst, cardFirst + CARD_PAGE_SIZE)

  // Liste kısalınca (silme, arama) açık sayfa boşta kalabilir; DURUMUN KENDİSİ başa alınır — yalnız
  // render değerini kırpmak yetmez, bayat `cardFirst` sonraki eklemede kullanıcıyı yanlış sayfaya atar.
  useEffect(() => {
    setCardFirst((f) => (f >= items.length ? 0 : f))
  }, [items.length])

  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  const submitting = create.isPending || update.isPending
  const busyId = changeStatus.isPending
    ? (changeStatus.variables?.id ?? null)
    : remove.isPending
      ? (remove.variables ?? null)
      : null

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openCreate = () => {
    setEditing(null)
    setDateError(null)
    setFormOpen(true)
  }
  const openEdit = (row: HolidayDay) => {
    setEditing(row)
    setDateError(null)
    setFormOpen(true)
  }

  // Domaine özel ele alma ekranda kalır; optimistic-lock ve genel hata toast merkezdedir.
  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setDateError(t('holidayDays.errors.duplicateDate'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: HolidayDayUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('holidayDays.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
      return
    }
    create.mutate(payload, {
      onSuccess: () => {
        notify('success', t('holidayDays.toast.created'))
        setFormOpen(false)
      },
      onError: handleMutationError,
    })
  }

  const onToggle = (row: HolidayDay, active: boolean) => {
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      { onSuccess: () => notify('success', t('holidayDays.toast.statusChanged')) },
    )
  }

  const onDelete = (row: HolidayDay) => {
    confirmDialog({
      header: t('holidayDays.delete.title'),
      message: t('holidayDays.delete.confirm', { name: row.descriptionTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('holidayDays.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('holidayDays.toast.deleted')),
        }),
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <HolidayDayFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        dateError={dateError}
        onClearDateError={() => setDateError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.admin.holidayDays')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={() => exportHolidayDays(debounced)} disabled={items.length === 0} />
            {!readOnly && (
              <Button icon="pi pi-plus" label={t('holidayDays.actions.new')} onClick={openCreate} />
            )}
          </>
        }
      />

      <FilterBar
        searchValue={search}
        searchPlaceholder={t('holidayDays.search')}
        onSearch={setSearch}
      />

      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && (
            <EmptyState
              title={t('holidayDays.empty.title')}
              description={t('holidayDays.empty.desc')}
            />
          )}
          {!list.isLoading &&
            cardItems.map((row) => {
              const active = row.recordStatus === 1
              return (
                <div key={row.id} className="ui-card">
                  <div className="ui-card-top">
                    <span className="ui-card-sub">{formatDate(row.holidayDate)}</span>
                    {!readOnly && (
                      <div className="ui-card-actions ui-card-actions--compact">
                        <InputSwitch
                          className="admin-switch"
                          checked={active}
                          disabled={busyId === row.id}
                          onChange={(e) => onToggle(row, Boolean(e.value))}
                          aria-label={row.descriptionTr}
                        />
                        <Button
                          icon="pi pi-pencil"
                          rounded
                          text
                          aria-label={t('holidayDays.actions.editNamed', { name: row.descriptionTr })}
                          onClick={() => openEdit(row)}
                        />
                        <Button
                          icon="pi pi-trash"
                          rounded
                          text
                          severity="danger"
                          aria-label={t('holidayDays.actions.deleteNamed', {
                            name: row.descriptionTr,
                          })}
                          disabled={busyId === row.id}
                          onClick={() => onDelete(row)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="ui-card-head">
                    <span className="ui-card-title">{localized(row.descriptionTr, row.descriptionEn)}</span>
                  </div>
                  <div className="ui-card-top">
                    <Badge tone={active ? 'green' : 'neutral'}>
                      {active ? t('holidayDays.status.active') : t('holidayDays.status.passive')}
                    </Badge>
                    {row.halfDay && (
                      <span className="ui-card-sub">{t('holidayDays.columns.halfDay')}</span>
                    )}
                  </div>
                </div>
              )
            })}
          {!list.isLoading && items.length > CARD_PAGE_SIZE && (
            <CardPaginator
              first={cardFirst}
              rows={CARD_PAGE_SIZE}
              totalRecords={items.length}
              onPage={setCardFirst}
            />
          )}
        </div>
      ) : (
        <DataTable
          value={items}
          loading={list.isLoading}
          sortField="holidayDate"
          sortOrder={-1}
          emptyMessage={
            <EmptyState
              title={t('holidayDays.empty.title')}
              description={t('holidayDays.empty.desc')}
            />
          }
        >
          {holidayDayColumns({
            t,
            localized,
            busyId,
            onEdit: openEdit,
            onToggle,
            onDelete,
            readOnly,
          })}
        </DataTable>
      )}
    </div>
  )
}
